import { useState, type ReactNode } from 'react';
import { navigate } from '../lib/router.ts';
import { useMe } from '../lib/me.ts';
import { LiveReturnBanner } from './LiveReturn.tsx';

/** In-app link: real <a href> for accessibility and new-tab, client-side navigation on click. */
export function Link({ to, className, children, ...rest }: { to: string; className?: string; children: ReactNode; 'aria-label'?: string }) {
  return (
    <a
      href={to}
      className={className}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}

export function Logo({ dark = false, size = 'md' }: { dark?: boolean; size?: 'sm' | 'md' }) {
  return (
    <Link to="/" className="flex items-center gap-2.5" aria-label="ZIP2LIVE home">
      <img src="/assets/branding/icon-192.png" alt="" className={`${size === 'sm' ? 'h-8 w-8' : 'h-9 w-9'} rounded-full bg-white`} />
      <span className={`speed-text ${size === 'sm' ? 'text-lg' : 'text-[22px]'} ${dark ? 'text-white' : 'text-navy'}`}>
        ZIP<span className="text-race">2</span>LIVE
      </span>
    </Link>
  );
}

/**
 * Site header. `tone` picks the ground: white (marketing), paper (app pages with a rule), dark (Go Live, viewer).
 * `right` replaces the default account link. `onSignIn` opens sign-in on the page instead of going to
 * /signin (the Go Live page, where leaving would stop the broadcast). Above it, a bar to get back to
 * a stream that is still on, except on the Go Live page itself (`streaming`).
 */
export function Header({ tone = 'white', right, onSignIn, streaming = false }: { tone?: 'white' | 'paper' | 'dark'; right?: ReactNode; onSignIn?: () => void; streaming?: boolean }) {
  const dark = tone === 'dark';
  return (
    <header className={tone === 'paper' ? 'border-b border-line bg-white' : dark ? 'text-white' : ''}>
      {!streaming && <LiveReturnBanner />}
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-8 sm:py-4">
        <Logo dark={dark} />
        <div className="flex shrink-0 items-center gap-1 whitespace-nowrap sm:gap-3">
          <Link to="/live" className={`inline-flex items-center gap-1.5 rounded-full px-2 py-2.5 text-sm font-bold sm:px-4 sm:text-[15px] ${dark ? 'text-white hover:bg-white/10' : 'text-navy hover:bg-mist'}`}>
            <span className="live-dot h-2 w-2 rounded-full bg-race" aria-hidden />
            Live<span className="hidden sm:inline">&nbsp;now</span>
          </Link>
          {right ?? <AccountLink dark={dark} onSignIn={onSignIn} />}
        </div>
      </div>
    </header>
  );
}

export function LiveBadge({ label = 'LIVE', small = false }: { label?: string; small?: boolean }) {
  return (
    <span className={`speed-text inline-flex items-center gap-1.5 bg-race text-white ${small ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm'}`}>
      <span className="live-dot h-2 w-2 rounded-full bg-white" aria-hidden />
      {label}
    </span>
  );
}

/** Round initial: the avatar of anyone without a profile photo (guests, and creators who haven't added one). */
export function Initial({ name, className = 'h-9 w-9 text-base' }: { name: string; className?: string }) {
  const letter = name.replace(/^@/, '').trim().charAt(0).toUpperCase() || 'Z';
  return (
    <span aria-hidden className={`inline-flex shrink-0 items-center justify-center rounded-full bg-race font-black text-white ${className}`}>
      {letter}
    </span>
  );
}

/**
 * A creator's avatar: their profile photo (a 512 px square on the media CDN), or their initial.
 * `className` sizes both, e.g. "h-9 w-9 text-base".
 */
export function Avatar({ name, photoUrl, className = 'h-9 w-9 text-base' }: { name: string; photoUrl?: string | null; className?: string }) {
  // A photo that fails to load (deleted, offline) falls back to the initial.
  const [broken, setBroken] = useState<string | null>(null);
  if (!photoUrl || broken === photoUrl) return <Initial name={name} className={className} />;
  return <img src={photoUrl} alt="" loading="lazy" decoding="async" onError={() => setBroken(photoUrl)} className={`shrink-0 rounded-full bg-navy-800 object-cover ${className}`} />;
}

export function Footer() {
  const col = 'flex flex-col gap-2.5 text-[15px] font-semibold text-white';
  const head = 'mb-1 text-xs font-extrabold tracking-[0.12em] text-faint-dark uppercase';
  return (
    <footer className="bg-navy-900 text-muted-dark">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:grid-cols-[1.4fr_1fr_1fr] sm:px-8">
        <div>
          <Logo dark size="sm" />
          <p className="mt-3 max-w-xs text-sm leading-relaxed">Live in seconds. Share anywhere. Streams are unlisted unless you list them on Live Now.</p>
          <a href="https://x.com/zip2live" target="_blank" rel="noopener me" className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2.5 text-sm font-bold text-white hover:bg-white/20">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
            Follow @zip2live
          </a>
        </div>
        <nav className={col} aria-label="Product">
          <p className={head}>Product</p>
          <Link to="/go-live" className="hover:text-race-300">Go live</Link>
          <Link to="/live" className="hover:text-race-300">Live now</Link>
          <Link to="/obs" className="hover:text-race-300">OBS setup</Link>
          <Link to="/account" className="hover:text-race-300">Your streams</Link>
          <Link to="/faq" className="hover:text-race-300">FAQ</Link>
        </nav>
        <nav className={col} aria-label="Company">
          <p className={head}>Company</p>
          <Link to="/contact" className="hover:text-race-300">Contact us</Link>
          <Link to="/privacy" className="hover:text-race-300">Privacy</Link>
          <Link to="/terms" className="hover:text-race-300">Terms of use</Link>
          <Link to="/copyright" className="hover:text-race-300">Copyright</Link>
          <a href="https://x.com/zip2live" target="_blank" rel="noopener me" className="hover:text-race-300">X / Twitter</a>
        </nav>
      </div>
      <div className="border-t border-white/10">
        <p className="mx-auto max-w-6xl px-4 py-5 text-[13px] sm:px-8">
          © {new Date().getFullYear()} ZIP2LIVE · Operated by{' '}
          <a href="https://jzw.dev" target="_blank" rel="noopener" className="font-bold text-white hover:text-race-300">jzw.dev</a>
        </p>
      </div>
    </footer>
  );
}

/** Top-right: "Sign in" for guests and first-time visitors, the creator's handle once signed in. */
function AccountLink({ dark, onSignIn }: { dark: boolean; onSignIn?: () => void }) {
  const { me } = useMe();
  const signedIn = me?.user?.isVerified;
  const label = signedIn ? (me?.channel?.handle ? `@${me.channel.handle}` : 'Account') : 'Sign in';
  const className = `rounded-full px-4 py-2.5 text-sm font-bold sm:text-[15px] ${dark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-mist text-navy hover:bg-line'}`;
  if (!signedIn && onSignIn) {
    return (
      <button type="button" onClick={onSignIn} className={className}>
        {label}
      </button>
    );
  }
  return (
    <Link to={signedIn ? '/account' : '/signin'} className={className}>
      {label}
    </Link>
  );
}
